import type { Door, SectorView } from '@somnio/protocol';
import { buildSpaceCollision, canStand, npcBodies } from './collision.ts';
import { reachDistance } from './combat.ts';
import { SOMNIO_CONSTANTS } from './constants.ts';
import { EDGE_TOLERANCE, distance, distanceToRect, modelToWorld, rectContains, rectsOverlap, worldToModel } from './geometry.ts';
import type { Point, Rect, Transform } from './geometry.ts';
import { SECTOR_BORDER_SUPPORT_METRES, paddedRasterRect } from './ground.ts';
import { heading, headingRadians } from './heading.ts';
import type { Heading } from './heading.ts';
import { objectModel } from './modelRegistry.ts';
import type { ModelRegistry } from './modelRegistry.ts';
import { monsterKind } from './monsterKinds.ts';
import { sectorPointInSpace, sectorRect, spawnArea } from './sector.ts';
import type { Sector } from './sector.ts';

/**
 * The world as spaces: every outdoor sector lies in one continuous space at its `origin`, and
 * each interior is a space of its own. Bodies walk freely within a space and change space through
 * a door, or by waking at the wake-point after giving up.
 */

export const OUTDOOR_SPACE_ID = 'outdoors';

/** How far out from its anchor, along its facing, a door puts an arriving body: beyond the trigger. */
const DOOR_ARRIVAL_OFFSET = 0.8;

export interface Space<S extends SectorView = SectorView> {
  id: string;
  sectors: S[];
}

/** A content mistake that leaves the world loadable, pinned to the record that carries it. */
export interface WorldIssue {
  sector: string;
  record: 'placement' | 'door' | 'sector';
  id: string;
  message: string;
}

export interface World {
  spaces: Map<string, Space<Sector>>;
  /** Sector name to the id of the space holding it. */
  sectorSpace: Map<string, string>;
  /**
   * What loading reported. A door reported for not being half of a sound pair is inert, and the
   * sectors in `spaces` do not carry it. A door reported because its arrival point lies within a
   * nightmare's reach stays live.
   */
  issues: WorldIssue[];
}

/** A world that cannot be served at all. */
export class WorldError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorldError';
  }
}

/**
 * Groups the sectors into spaces and proves what a join relies on. Throws on outdoor sectors that
 * overlap, an outdoor sector smaller than `minOutdoorSectorExtent`, a sector named like the
 * outdoor space, and a `spawn` a body cannot stand on. A door is live only as half of a sound
 * pair: both doors resolve, point at each other, and have an arrival point a body can stand on.
 * Any other door is reported and dropped from its sector. A live door whose arrival point lies
 * within a nightmare's reach of a monster spawn's area in its space is reported and kept. The
 * collision issues of each space are reported too, and so is every pair of outdoor sectors that
 * stand too close to blend their grounds evenly without touching. Standing means clear of
 * colliders, blockers, ledges, and the space's NPCs.
 */
export function buildWorld(sectors: readonly Sector[], registry: ModelRegistry): World {
  const outdoor = sectors.filter((sector) => sector.kind === 'outdoor');
  for (const sector of sectors) {
    if (sector.name === OUTDOOR_SPACE_ID) throw new WorldError(`a sector cannot be named "${OUTDOOR_SPACE_ID}"`);
  }
  outdoor.forEach((sector, index) => {
    if (Math.min(sector.size.width, sector.size.depth) < SOMNIO_CONSTANTS.minOutdoorSectorExtent) {
      throw new WorldError(`outdoor sector ${sector.name} is smaller than ${SOMNIO_CONSTANTS.minOutdoorSectorExtent} metres`);
    }
    const overlapped = outdoor.slice(index + 1).find((other) => rectsOverlap(sectorRect(sector), sectorRect(other)));
    if (overlapped !== undefined) throw new WorldError(`outdoor sectors ${sector.name} and ${overlapped.name} overlap`);
  });

  const issues: WorldIssue[] = spacingIssues(outdoor);

  const groups: Space<Sector>[] = [
    ...(outdoor.length === 0 ? [] : [{ id: OUTDOOR_SPACE_ID, sectors: outdoor }]),
    ...sectors.filter((sector) => sector.kind === 'interior').map((sector) => ({ id: sector.name, sectors: [sector] })),
  ];
  const standsIn = new Map<string, (point: Point) => boolean>();
  for (const space of groups) {
    const collision = buildSpaceCollision(space, registry);
    // Not a spread: a space's issues can outnumber the arguments one call may take.
    for (const issue of collision.issues) issues.push(issue);
    const npcs = npcBodies(space);
    const stands = (point: Point): boolean => canStand(collision, point, npcs);
    for (const sector of space.sectors) standsIn.set(sector.name, stands);
  }

  for (const sector of sectors) {
    if (sector.spawn === undefined) continue;
    if (!standsIn.get(sector.name)!(sectorPointInSpace(sector, sector.spawn))) {
      throw new WorldError(`the spawn of sector ${sector.name} at (${sector.spawn.x}, ${sector.spawn.z}) is not clear`);
    }
  }

  const byName = new Map(sectors.map((sector) => [sector.name, sector]));
  const unsound = new Map<Door, string>();
  const arrivals = new Map<Door, Point>();
  for (const sector of sectors) {
    for (const door of sector.doors) {
      const resolved = resolveDoor(sector, door, registry);
      if (resolved === undefined) {
        unsound.set(door, `placement "${door.placement}" has no door anchor "${door.anchor}"`);
      } else if (!standsIn.get(sector.name)!(resolved.arrival)) {
        unsound.set(door, `the arrival point (${resolved.arrival.x.toFixed(2)}, ${resolved.arrival.z.toFixed(2)}) is not clear`);
      } else {
        arrivals.set(door, resolved.arrival);
      }
    }
  }
  const inert = new Set<Door>();
  for (const sector of sectors) {
    for (const door of sector.doors) {
      const targetName = `"${door.target.door}" in ${door.target.sector}`;
      const target = byName.get(door.target.sector)?.doors.find((candidate) => candidate.id === door.target.door);
      let message = unsound.get(door);
      if (message === undefined) {
        if (target === undefined) message = `target door ${targetName} does not exist`;
        else if (target.target.sector !== sector.name || target.target.door !== door.id) message = `target door ${targetName} does not point back`;
        else if (unsound.has(target)) message = `target door ${targetName} is inert`;
      }
      if (message === undefined) continue;
      inert.add(door);
      issues.push({ sector: sector.name, record: 'door', id: door.id, message });
    }
  }
  for (const space of groups) {
    // Where a nightmare's centre can be: its area widened by EDGE_TOLERANCE on every side, as the simulation's containment test allows.
    const grounds = space.sectors.flatMap((sector) =>
      sector.monsterSpawns.map((spawn) => {
        const area = spawnArea(sector, spawn);
        const width = area.width + 2 * EDGE_TOLERANCE;
        const depth = area.depth + 2 * EDGE_TOLERANCE;
        return { sector: sector.name, spawn, standing: { x: area.x - EDGE_TOLERANCE, z: area.z - EDGE_TOLERANCE, width, depth } };
      }),
    );
    for (const sector of space.sectors) {
      for (const door of sector.doors) {
        const arrival = arrivals.get(door);
        if (arrival === undefined || inert.has(door)) continue;
        // One issue a door, however many spawns reach it, so the issues grow with the doors and not with doors times spawns.
        const near = grounds.find(({ spawn, standing }) => distanceToRect(arrival, standing) <= reachDistance(monsterKind(spawn.kind).radius));
        if (near === undefined) continue;
        const message = `the arrival point (${arrival.x.toFixed(2)}, ${arrival.z.toFixed(2)}) lies within reach of monster spawn "${near.spawn.id}" in ${near.sector}`;
        issues.push({ sector: sector.name, record: 'door', id: door.id, message });
      }
    }
  }

  const spaces = new Map<string, Space<Sector>>();
  const sectorSpace = new Map<string, string>();
  for (const space of groups) {
    spaces.set(space.id, { id: space.id, sectors: space.sectors.map((sector) => ({ ...sector, doors: sector.doors.filter((door) => !inert.has(door)) })) });
    for (const sector of space.sectors) sectorSpace.set(sector.name, space.id);
  }
  return { spaces, sectorSpace, issues };
}

/**
 * A client is sent only the sectors touching the player's, so it can blend a sector's ground only
 * with those. A sector that does not touch must therefore stand clear of everything the blend
 * computes for the other: its background paints `SECTOR_BORDER_SUPPORT_METRES` past its rect, and
 * the other's raster reaches past its rect by the gutter. A pair too close is reported under each
 * of the two; the world still loads, and only its seam is uneven.
 */
function spacingIssues(outdoor: Sector[]): WorldIssue[] {
  const issues: WorldIssue[] = [];
  const space = { id: OUTDOOR_SPACE_ID, sectors: outdoor };
  for (const sector of outdoor) {
    const touching = new Set(neighbourSectors(space, sector.name));
    const raster = paddedRasterRect(sector);
    for (const other of outdoor) {
      if (other === sector || touching.has(other)) continue;
      const gap = Math.min(rectDistance(raster, sectorRect(other)), rectDistance(sectorRect(sector), paddedRasterRect(other)));
      if (gap >= SECTOR_BORDER_SUPPORT_METRES) continue;
      issues.push({
        sector: sector.name,
        record: 'sector',
        id: other.name,
        message: `stands ${gap.toFixed(2)} m from ${other.name} without touching it, closer than the ${SECTOR_BORDER_SUPPORT_METRES} m their grounds blend over`,
      });
    }
  }
  return issues;
}

function rectDistance(a: Rect, b: Rect): number {
  const dx = Math.max(0, a.x - (b.x + b.width), b.x - (a.x + a.width));
  const dz = Math.max(0, a.z - (b.z + b.depth), b.z - (a.z + a.depth));
  return Math.hypot(dx, dz);
}

/** The sector whose ground holds the point, in the space's coordinates. */
export function sectorAt<S extends SectorView>(space: Space<S>, point: Point): S | undefined {
  return space.sectors.find((sector) => rectContains(sectorRect(sector), point, EDGE_TOLERANCE));
}

/** The sectors whose ground touches the named sector's, along an edge or at a corner. */
export function neighbourSectors<S extends SectorView>(space: Space<S>, sectorName: string): S[] {
  const sector = space.sectors.find((candidate) => candidate.name === sectorName);
  if (sector === undefined) return [];
  const rect = sectorRect(sector);
  return space.sectors.filter((candidate) => {
    const other = sectorRect(candidate);
    return (
      candidate !== sector &&
      other.x - (rect.x + rect.width) <= EDGE_TOLERANCE &&
      rect.x - (other.x + other.width) <= EDGE_TOLERANCE &&
      other.z - (rect.z + rect.depth) <= EDGE_TOLERANCE &&
      rect.z - (other.z + other.depth) <= EDGE_TOLERANCE
    );
  });
}

export interface ResolvedDoor {
  /** The door's anchor in the space's coordinates: the opening itself, where a voice through it comes out. */
  doorway: Point;
  /** Where a body coming out of the door stands, in the space's coordinates. */
  arrival: Point;
  /** The heading out of the door, which an arriving body faces. */
  facing: Heading;
  /** Where the door's placement stands in the space. */
  transform: Transform;
  /** The ground in front of the door that uses it, in the placement's model space. */
  trigger: Rect;
}

/** `undefined` when the door's placement is missing, its model is not in the registry, or the model has no such anchor. */
export function resolveDoor(sector: SectorView, door: Door, registry: ModelRegistry): ResolvedDoor | undefined {
  const placement = sector.placements.find((candidate) => candidate.id === door.placement);
  if (placement === undefined) return undefined;
  const anchor = objectModel(registry, placement.modelId)?.doors.find((candidate) => candidate.id === door.anchor);
  if (anchor === undefined) return undefined;
  const transform = { ...sectorPointInSpace(sector, placement), yaw: placement.yaw };
  const radians = headingRadians(anchor.facing);
  const out = { x: Math.round(Math.sin(radians)), z: Math.round(Math.cos(radians)) };
  const depth = SOMNIO_CONSTANTS.doorTriggerDepth;
  const reach = { x: out.x * depth, z: out.z * depth };
  const across = { x: (Math.abs(out.z) * anchor.width) / 2, z: (Math.abs(out.x) * anchor.width) / 2 };
  return {
    doorway: modelToWorld(transform, anchor),
    arrival: modelToWorld(transform, { x: anchor.x + out.x * DOOR_ARRIVAL_OFFSET, z: anchor.z + out.z * DOOR_ARRIVAL_OFFSET }),
    facing: heading(anchor.facing + placement.yaw),
    transform,
    trigger: {
      x: anchor.x - across.x + Math.min(0, reach.x),
      z: anchor.z - across.z + Math.min(0, reach.z),
      width: 2 * across.x + Math.abs(reach.x),
      depth: 2 * across.z + Math.abs(reach.z),
    },
  };
}

/** Whether the point stands in the door's trigger, widened by `slack` on every side. */
export function doorContains(resolved: ResolvedDoor, point: Point, slack: number): boolean {
  return rectContains(resolved.trigger, worldToModel(resolved.transform, point), slack);
}

/** Whether a dreamer stands within speaking distance of an NPC: where it greets them, answers a `talk`, and keeps a master's offer open. */
export function withinSpeakingDistance(dreamer: Point, npc: Point): boolean {
  return distance(dreamer, npc) <= SOMNIO_CONSTANTS.npcInteractionRadius;
}
